// src/.../BalanceOverTimeChart/BalanceOverTimeChart.jsx
import React, { useMemo, useState } from "react";
import { ChartBox, cssVar } from "../ChartPrimitives/ChartPrimitives";
import PeriodDropdown from "../PeriodDropdown/PeriodDropdown";
import { inferAccountDelta } from "../../../../../../app/app-utils/txDelta";

/* currency fmt */
function formatMaybeCurrency(n, currencyCode) {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return String(n ?? "");
  try {
    if (!currencyCode) return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode, maximumFractionDigits: 2 }).format(num);
  } catch { return num.toFixed(2); }
}

/* tiny date helpers (UTC-safe ISO) */
const toISO = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString().slice(0, 10);
function addDaysISO(iso, days) { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + days); return toISO(d); }
function eachDayISO(fromISO, toISO) { const out = []; if (!fromISO || !toISO) return out; let cur = fromISO; while (cur <= toISO) { out.push(cur); cur = addDaysISO(cur, 1); } return out; }
function weekStartISO(iso) { const d = new Date(`${iso}T00:00:00Z`); const day = d.getUTCDay(); const delta = (day + 6) % 7; d.setUTCDate(d.getUTCDate() - delta); return toISO(d); }
const monthKey = (iso) => iso.slice(0, 7);

/* resample: keep last point of bucket */
function resample(labels, values, period) {
  if (!labels.length || labels.length !== values.length) return { labels: [], values: [] };
  if (period === "day") return { labels, values };
  if (period === "week") {
    const m = new Map();
    for (let i = 0; i < labels.length; i++) m.set(weekStartISO(labels[i]), { iso: labels[i], val: values[i] });
    const keys = Array.from(m.keys()).sort();
    return { labels: keys.map(k => m.get(k).iso), values: keys.map(k => m.get(k).val) };
  }
  const m = new Map();
  for (let i = 0; i < labels.length; i++) m.set(monthKey(labels[i]), { iso: labels[i], val: values[i] });
  const keys = Array.from(m.keys()).sort();
  return { labels: keys.map(k => m.get(k).iso), values: keys.map(k => m.get(k).val) };
}

/* fast reconstruction from prefiltered accountRows (asc) */
function buildDailyFromAccountRows({ accountRows = [], account, resolvedRange, fallbackDays = 90 }) {
  if (!account?.id || !accountRows.length) return { labels: [], values: [] };

  const acctId = account.id;
  const rowsAsc = accountRows;               // already asc & contains _iso
  const lastISO = rowsAsc[rowsAsc.length - 1]._iso;
  const toISO = resolvedRange?.to || lastISO;
  const fromISO = resolvedRange?.from || (() => {
    const minISO = rowsAsc[0]._iso;
    const fallbackFrom = addDaysISO(toISO, -fallbackDays + 1);
    return minISO > fallbackFrom ? minISO : fallbackFrom;
  })();

  // rewind anything AFTER 'to'
  let afterSum = 0;
  for (let i = rowsAsc.length - 1; i >= 0; i--) {
    const r = rowsAsc[i];
    if (r._iso <= toISO) break;
    afterSum += inferAccountDelta(r, acctId);
  }
  const endBalance =
    Number(account.accountBalance ?? account.balance ?? account.currentBalance ?? 0) - afterSum;

  // accumulate deltas within range
  const perDay = new Map();
  let rangeSum = 0;
  let s = 0; while (s < rowsAsc.length && rowsAsc[s]._iso < fromISO) s++;
  let e = rowsAsc.length - 1; while (e >= 0 && rowsAsc[e]._iso > toISO) e--;
  for (let i = s; i <= e; i++) {
    const r = rowsAsc[i];
    const d = inferAccountDelta(r, acctId);
    perDay.set(r._iso, (perDay.get(r._iso) || 0) + d);
    rangeSum += d;
  }

  const labels = eachDayISO(fromISO, toISO);
  if (!labels.length) return { labels: [], values: [] };

  let running = endBalance - rangeSum;     // start-of-range
  const values = new Array(labels.length);
  for (let i = 0; i < labels.length; i++) {
    running += (perDay.get(labels[i]) || 0);
    values[i] = running;
  }
  return { labels, values };
}

/* prefer table-attached per-row balances (matches your “last working” view) */
function buildFromRowBalances(items = []) {
  const byDay = new Map(); // last value per day
  for (const row of items) {
    const iso = String(row?.date || "").slice(0, 10);
    const bal = Number(row?.balance);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) || !Number.isFinite(bal)) continue;
    byDay.set(iso, bal); // overwrite -> keep last in display order
  }
  if (!byDay.size) return { labels: [], values: [] };
  const labels = Array.from(byDay.keys()).sort();           // asc
  const values = labels.map(d => byDay.get(d));
  return { labels, values };
}

export default function BalanceOverTimeChart({
  items,                 // filtered table rows (contain `balance` when an account is selected)
  accountRows = [],      // prefiltered asc account rows with _iso
  account,
  resolvedRange,
  defaultPeriod = "week",
  currencyCode,
}) {
  const [period, setPeriod] = useState(defaultPeriod);

  // 1) Try to use the same balance numbers you see in the table (parity with past behavior)
  const rowSeries = useMemo(() => buildFromRowBalances(items), [items]);

  // 2) If unavailable/empty, compute from all account rows (correct, full-fidelity)
  const computedSeries = useMemo(
    () => buildDailyFromAccountRows({ accountRows, account, resolvedRange }),
    [accountRows, account?.id, account?.accountBalance, account?.balance, account?.currentBalance, resolvedRange?.from, resolvedRange?.to]
  );

  const daySeries = rowSeries.labels.length ? rowSeries : computedSeries;

  const series = useMemo(
    () => resample(daySeries.labels, daySeries.values, period),
    [daySeries.labels, daySeries.values, period]
  );

  const data = useMemo(() => ({
    labels: series.labels,
    datasets: [{
      label: "Balance",
      data: series.values,
      borderWidth: 2,
      tension: 0.32,
      borderColor: cssVar("--pp-chart-accent", "#5b5ce2"),
      pointRadius: 0,
      pointHitRadius: 8,
      fill: false,
    }]
  }), [series.labels, series.values]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${formatMaybeCurrency(ctx.parsed.y, currencyCode)}` } },
      zoom: { zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: "x" }, pan: { enabled: true, mode: "x" } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: cssVar("--pp-chart-tick", "#6b7280"), maxTicksLimit: 12 } },
      y: { grid: { color: cssVar("--pp-chart-grid", "rgba(0,0,0,.06)") }, ticks: { color: cssVar("--pp-chart-tick", "#6b7280") } }
    }
  }), [currencyCode]);

  return (
    <ChartBox
      title="Balance over time"
      type="line"
      data={data}
      options={options}
      height={260}
      rightNode={<PeriodDropdown value={period} onChange={setPeriod} />}
    />
  );
}
